import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Check, Phone } from 'lucide-react';
import { api, type EventSwapPayload } from '@/lib/api';
import { cn } from '@/lib/utils';
import PlayerHubHeader from '@/components/PlayerHubHeader';

export default function PlayerHubSwap() {
  const params = useParams<{ token: string; eventId: string }>();
  const token = params.token ?? '';
  const eventId = Number(params.eventId ?? 0);
  const qc = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['self-report-me', token, '90d'],
    queryFn: () => api.getSelfReportMe(token),
    staleTime: 60_000,
    enabled: !!token,
    retry: false,
  });

  const swapQuery = useQuery({
    queryKey: ['event-swap', eventId, token],
    queryFn: () => api.getEventSwap(token, eventId),
    enabled: !!token && Number.isFinite(eventId) && eventId > 0,
    retry: false,
  });

  const patch = useMutation({
    mutationFn: (body: { contactMade?: boolean; aim15Min?: boolean }) =>
      api.patchEventSwap(token, eventId, body),
    onSuccess: (data) => {
      qc.setQueryData(['event-swap', eventId, token], data);
      qc.invalidateQueries({ queryKey: ['event-swap', eventId, token] });
    },
  });

  const playerLabel =
    meQuery.data?.player.displayName?.trim() || meQuery.data?.player.name || '';
  const playerRank = meQuery.data?.player.rank ?? '';

  if (swapQuery.isLoading) {
    return (
      <Shell playerLabel={playerLabel} playerRank={playerRank} token={token}>
        <div className="text-slate-400">Loading…</div>
      </Shell>
    );
  }

  if (swapQuery.isError || !swapQuery.data) {
    const message =
      (swapQuery.error as Error | undefined)?.message ??
      'This event is not available for swap coordination.';
    return (
      <Shell playerLabel={playerLabel} playerRank={playerRank} token={token}>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
          {message}
        </div>
      </Shell>
    );
  }

  const data = swapQuery.data;
  const cp = data.counterpart;

  return (
    <Shell playerLabel={playerLabel} playerRank={playerRank} token={token}>
      <EventCard data={data} />
      {!cp ? (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-sm text-slate-300">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Counterpart
          </div>
          No counterpart is assigned for this slot yet — nothing to coordinate.
          Check back once the roster is finalized.
        </div>
      ) : (
        <>
          <CounterpartCard data={data} />
          <MutualToggle
            icon={<Phone size={14} />}
            title="We have been in contact"
            myLabel="I confirm we've been in contact"
            myState={data.me.contactMade}
            counterpartName={cp.name}
            counterpartState={cp.contactMade}
            disabled={patch.isPending}
            onToggle={() => patch.mutate({ contactMade: !data.me.contactMade })}
            hint="Please coordinate with each other about the swap. Aim to swap at roughly the 15-minute mark of the slot — that way both of you get a fair shot at points."
          />
        </>
      )}
      {patch.isError && (
        <div className="rounded-lg border border-rose-800 bg-rose-950/40 p-3 text-xs text-rose-200">
          {(patch.error as Error).message}
        </div>
      )}
    </Shell>
  );
}

function Shell({
  playerLabel,
  playerRank,
  token,
  children,
}: {
  playerLabel: string;
  playerRank: string;
  token: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <PlayerHubHeader
        token={token}
        playerLabel={playerLabel}
        playerRank={playerRank}
        subtitle="Swap coordination"
      />
      <main className="mx-auto max-w-md space-y-4 px-4 py-6">{children}</main>
    </div>
  );
}

function EventCard({ data }: { data: EventSwapPayload }) {
  const shortType = data.event.type === 'canyon_storm' ? 'CS' : 'DS';
  const roleLabel = data.me.role === 'starter' ? 'Starter' : 'Sub';
  const positionLabel = data.me.position != null ? ` #${data.me.position}` : '';
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded bg-indigo-900/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-100">
          {shortType}
        </span>
        <span className="text-xs text-slate-400">Slot {data.event.slot}</span>
      </div>
      <div className="mt-2 text-sm text-slate-200">
        {data.event.eventDate}
        {data.event.startsAtServer ? ` · ${data.event.startsAtServer} server` : ''}
      </div>
      <div className="mt-1 text-xs text-slate-400">
        You are: {roleLabel}
        {positionLabel}
      </div>
    </section>
  );
}

function CounterpartCard({ data }: { data: EventSwapPayload }) {
  const cp = data.counterpart!;
  const cpRole = cp.role === 'starter' ? 'Starter' : 'Sub';
  const positionLabel = cp.position != null ? ` #${cp.position}` : '';
  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
        Counterpart ({cpRole}
        {positionLabel})
      </div>
      <div className="mt-1 text-base font-semibold text-slate-100">{cp.name}</div>
      <div className="text-xs text-slate-400">
        {cp.timezone ? cp.timezone : 'Timezone not set'}
      </div>
    </section>
  );
}

function MutualToggle({
  icon,
  title,
  myLabel,
  myState,
  counterpartName,
  counterpartState,
  disabled,
  onToggle,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  myLabel: string;
  myState: boolean;
  counterpartName: string;
  counterpartState: boolean;
  disabled: boolean;
  onToggle: () => void;
  hint?: string;
}) {
  const both = myState && counterpartState;
  const statusLabel = both
    ? 'Confirmed by both sides'
    : myState
      ? `Waiting for ${counterpartName} to confirm`
      : counterpartState
        ? `${counterpartName} has confirmed — waiting on you`
        : 'Not yet confirmed by either side';
  const statusColor = both
    ? 'text-emerald-300'
    : myState || counterpartState
      ? 'text-amber-300'
      : 'text-slate-400';

  return (
    <section
      className={cn(
        'rounded-lg border p-4 transition',
        both
          ? 'border-emerald-700/60 bg-emerald-950/30'
          : 'border-slate-800 bg-slate-900/40',
      )}
    >
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-300">
        {icon}
        {title}
      </div>
      <div className={cn('mb-3 text-xs font-medium', statusColor)}>{statusLabel}</div>

      {hint && (
        <div className="mb-3 rounded-md border border-indigo-800/50 bg-indigo-950/40 p-3 text-[11px] leading-relaxed text-indigo-100">
          {hint}
        </div>
      )}

      <div className="mb-2 space-y-1.5">
        <ConfirmedRow name={`${counterpartName} (counterpart)`} on={counterpartState} />
        <ConfirmedRow name="You" on={myState} />
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={onToggle}
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm transition disabled:opacity-60',
          myState
            ? 'border-indigo-500 bg-indigo-600/20 text-indigo-100'
            : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-500',
        )}
      >
        <span>{myLabel}</span>
        <span
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded border text-[10px] font-semibold',
            myState
              ? 'border-indigo-300 bg-indigo-500 text-white'
              : 'border-slate-600 text-slate-500',
          )}
        >
          {myState ? '✓' : ''}
        </span>
      </button>

    </section>
  );
}

function ConfirmedRow({ name, on }: { name: string; on: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded border px-2.5 py-1 text-xs',
        on
          ? 'border-emerald-700/50 bg-emerald-900/20 text-emerald-100'
          : 'border-slate-800 bg-slate-950/40 text-slate-400',
      )}
    >
      <span>{name}</span>
      <span className="inline-flex items-center gap-1">
        {on ? (
          <>
            <Check size={12} /> confirmed
          </>
        ) : (
          'pending'
        )}
      </span>
    </div>
  );
}
